import React, { useState, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import ReactTableConstructor from "./components/ReactTableConstructor";
import ReactTableCell from "./components/ReactTableCell";
import ReactTableCellEditable from "./components/ReactTableEditableCell";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import ReactTableConstructorClient from "./components/ReactTableConstructorClient";
import PartsTableConstructor from "./components/PartsTableConstructor";

const ReactTableBase = React.memo(
  ({
    tableConfig,
    columns,
    data,
    updateDraggableData,
    updateEditableData,
    valuesArray,
    initialData,
    setData,
    localFilters,
    setLocalFilters,
    pauses,
    setSnackbarMsg,
  }) => {
    const location = useLocation();
    const path = location.pathname;

    // Memoize screen detection
    const screen = useMemo(() => path.slice(1, path.length), [path]);

    // Memoize server-side fetching logic
    const serverSideFetching = useMemo(() => {
      const clientSideScreens = [
        "inspectiontasks",
        "inspectionforms",
        "inspectionscheduling",
        "servicetasks",
        "expensetasks",
        "users",
        //"vehicles",
        "dailytimesheet",
        "faultcodes",
      ];
      return !clientSideScreens.some((screenName) =>
        screen.includes(screenName)
      );
    }, [screen]);

    const [rightPanelData, setRightPanelData] = useState({
      show: false,
      content: "",
    });

    const {
      isEditable,
      isResizable,
      isSortable,
      withDragAndDrop,
      withPagination,
      withSearchEngine,
      manualPageSize,
    } = tableConfig;

    const length = useSelector((state) => state.filter.data.length);
    const totalRecords = useSelector((state) => state.filter.totalRecords);

    // Memoize sort configuration
    const sortByObj = useMemo(() => {
      const sortConfigs = {
        dailymileage: { id: 3, desc: true },
        workorders: { id: 4, desc: true },
        vehicles: { id: 1, desc: false },
        equipment: { id: 1, desc: false },
        users: { id: 1, desc: false },
        faultcodes: { id: 1, desc: false },
        issues: { id: 6, desc: true },
        maintenancereminders: { id: 3, desc: true },
        renewals: { id: 3, desc: true },
        inspectionscheduling: { id: 3, desc: true },
        dailytimesheet: { id: 3, desc: true },
        servicetasks: { id: 2, desc: false },
        expensetasks: { id: 2, desc: false },
        inspectiontasks: { id: 1, desc: false },
        inspectionforms: { id: 1, desc: false },
        inspectionsubmissions: { id: 3, desc: true },
        parts: { id: 1, desc: false },
        usage: { id: 3, desc: true },
      };

      for (const [key, config] of Object.entries(sortConfigs)) {
        if (screen.includes(key)) {
          return config;
        }
      }
      return { id: 2, desc: true };
    }, [screen]);

    const [filterValue, setFilterValue] = useState(null);

    // Memoize table options
    const tableOptions = useMemo(
      () => ({
        columns,
        data,
        updateDraggableData,
        updateEditableData,
        setFilterValue,
        defaultColumn: {},
        isEditable,
        withDragAndDrop: withDragAndDrop || false,
        dataLength: data.length,
        autoResetPage: false,
        disableSortBy: !isSortable,
        manualSortBy: true,
        manualGlobalFilter: true,
        manualPagination: true,
        pageCount: Math.ceil(totalRecords / length),
        initialState: {
          pageIndex: 0,
          pageSize: 25,
          globalFilter:
            withSearchEngine && filterValue ? filterValue : undefined,
          sortBy: [sortByObj],
          columnOrder: [1, 2, 3, 4, 5, 6],
        },
        disableSortRemove: true,
      }),
      [
        columns,
        data,
        updateDraggableData,
        updateEditableData,
        isEditable,
        withDragAndDrop,
        isSortable,
        totalRecords,
        length,
        withSearchEngine,
        filterValue,
        sortByObj,
      ]
    );

    const tableOptionsClient = useMemo(
      () => ({
        columns,
        data,
        updateDraggableData,
        updateEditableData,
        setFilterValue,
        defaultColumn: {},
        isEditable,
        withDragAndDrop: withDragAndDrop || false,
        dataLength: data.length,
        autoResetPage: false,
        disableSortBy: !isSortable,
        manualSortBy: !isSortable,
        manualGlobalFilter: !withSearchEngine,
        manualPagination: false,
        initialState: {
          pageIndex: 0,
          pageSize: manualPageSize ? manualPageSize[0] : 10,
          globalFilter:
            withSearchEngine && filterValue ? filterValue : undefined,
          sortBy: [sortByObj],
        },
        disableSortRemove: true,
      }),
      [
        columns,
        data,
        updateDraggableData,
        updateEditableData,
        isEditable,
        withDragAndDrop,
        isSortable,
        withSearchEngine,
        manualPageSize,
        filterValue,
        sortByObj,
      ]
    );

    // Memoize default column configuration
    const defaultColumn = useMemo(() => {
      if (isEditable) {
        return { Cell: ReactTableCellEditable };
      }
      if (withSearchEngine) {
        return { Cell: ReactTableCell };
      }
      return {};
    }, [isEditable, withSearchEngine]);

    // Update table options with default column
    const finalTableOptions = useMemo(
      () => ({
        ...tableOptions,
        defaultColumn,
      }),
      [tableOptions, defaultColumn]
    );

    const finalTableOptionsClient = useMemo(
      () => ({
        ...tableOptionsClient,
        defaultColumn,
      }),
      [tableOptionsClient, defaultColumn]
    );

    // Memoize right panel data setter
    const setRightPanelDataCallback = useCallback((data) => {
      setRightPanelData(data);
    }, []);

    return (
      <>
        {serverSideFetching ? (
          <>
            {screen.includes("parts") ? (
              <PartsTableConstructor
                tableConfig={tableConfig}
                tableOptions={finalTableOptions}
                valuesArray={valuesArray}
                initialData={initialData}
                screen={screen}
                setData={setData}
                rightPanelData={rightPanelData}
                setRightPanelData={setRightPanelDataCallback}
                setSnackbarMsg={setSnackbarMsg}
              />
            ) : (
              <ReactTableConstructor
                tableConfig={tableConfig}
                tableOptions={finalTableOptions}
                valuesArray={valuesArray}
                initialData={initialData}
                screen={screen}
                setData={setData}
                rightPanelData={rightPanelData}
                setRightPanelData={setRightPanelDataCallback}
                pauses={pauses}
              />
            )}
          </>
        ) : (
          <ReactTableConstructorClient
            tableConfig={tableConfig}
            tableOptions={finalTableOptionsClient}
            valuesArray={valuesArray}
            initialData={initialData}
            screen={screen}
            data={data}
            setData={setData}
            localFilters={localFilters}
            setLocalFilters={setLocalFilters}
            rightPanelData={rightPanelData}
            setRightPanelData={setRightPanelDataCallback}
          />
        )}
      </>
    );
  }
);

ReactTableBase.propTypes = {
  tableConfig: PropTypes.shape({
    isEditable: PropTypes.bool,
    isResizable: PropTypes.bool,
    isSortable: PropTypes.bool,
    withDragAndDrop: PropTypes.bool,
    withPagination: PropTypes.bool,
    withSearchEngine: PropTypes.bool,
    manualPageSize: PropTypes.arrayOf(PropTypes.number),
  }),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      name: PropTypes.string,
    })
  ),
  data: PropTypes.arrayOf(PropTypes.shape()),
  updateDraggableData: PropTypes.func,
  updateEditableData: PropTypes.func,
};

ReactTableBase.defaultProps = {
  tableConfig: {
    isEditable: false,
    isResizable: false,
    isSortable: false,
    withDragAndDrop: false,
    withPagination: false,
    withSearchEngine: false,
    manualPageSize: [],
  },
  columns: [
    { Header: "#", accessor: "id" },
    { Header: "Header Example Title one", accessor: "first" },
    { Header: "Header Example Title two", accessor: "last" },
  ],
  data: [
    { id: 1, first: "Cell Example Data one", last: "Cell Example Data two" },
    { id: 2, first: "Cell Example Data three", last: "Cell Example Data four" },
  ],
  updateDraggableData: () => {},
  updateEditableData: () => {},
};

export default ReactTableBase;
